import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  Merediem,
  nameof,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { AmountType, PromotionStatus } from "../enums/Promotion.enum"; 
import {
  IAPICheckUniquesnessModel,
  IAPIPromotionGrid,
  IAPIPromotionModel,
  IAPIUpdatePromotionModel,
} from "../interfaces/IAPIPromotionModels";
import { autorun } from "mobx";

export class PromotionGridModel {
  id: string;
  name: string;
  status: keyof typeof PromotionStatus;
  promoCode: string;
  lastUsed: Date | null;
  restrictions: boolean;
  isActive: boolean;

  constructor(data?: Partial<PromotionGridModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.promoCode = data?.promoCode || "";
    this.status = data?.status || PromotionStatus.Active;
    this.lastUsed = data?.lastUsed || null;
    this.restrictions = data?.restrictions || false;
    this.isActive = data?.isActive || false;
  }

  static deserializeAPIModel(data: IAPIPromotionGrid) {
    if (!data) return new PromotionGridModel();
    const mappedData: Partial<PromotionGridModel> = {
      id: data.id,
      name: data.name,
      promoCode: data.code,
      status: data.status,
      lastUsed: data.lastUsed
        ? UtilityMethods.removeZFromDateString(data.lastUsed)
        : null,
      restrictions: data.restrictions,
      isActive: data.isActive,
    };
    return new PromotionGridModel(mappedData);
  }
}

export class PromotionModel {
  id: string;
  name: string;
  promoCode: string;
  amount: number;
  amountType: keyof typeof AmountType;
  freeShipping: boolean;
  waiveSurcharge: boolean;
  minimumPurchase: number | null;
  totalUsage: number | null;
  beginsDate: Date | null;
  beginsTime: string;
  beginsMeredium: keyof typeof Merediem;
  endsDate: Date | null;
  endsTime: string;
  endsMeredium: keyof typeof Merediem;
  maximumAmount: number;
  lastUsed: Date | null;
  remainingUsage: number;
  errors: InputErrorModel<PromotionModel>[];

  constructor(data?: Partial<PromotionModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.promoCode = data?.promoCode || "";
    this.amount = data?.amount ?? 0;
    this.amountType = data?.amountType || "Amount";
    this.freeShipping = data?.freeShipping || false;
    this.waiveSurcharge = data?.waiveSurcharge || false;
    this.minimumPurchase = data?.minimumPurchase || null;
    this.totalUsage = data?.totalUsage || null;
    this.beginsDate = data?.beginsDate || null;
    this.beginsTime = data?.beginsTime ?? "12:00";
    this.beginsMeredium = data?.beginsMeredium || "AM";
    this.endsDate = data?.endsDate || null;
    this.endsTime = data?.endsTime ?? "11:59";
    this.endsMeredium = data?.endsMeredium || "PM";
    this.maximumAmount = data?.maximumAmount ?? 0;
    this.lastUsed = data?.lastUsed || null;
    this.remainingUsage = data?.remainingUsage || 0;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<PromotionModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasErrorInDiscountSection(): boolean {
    return (
      this.hasError("amount") ||
      this.hasError("amountType") ||
      this.hasError("maximumAmount") ||
      this.hasError("name") ||
      this.hasError("promoCode")
    );
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PromotionModel>[] = [];
    CoreInputModelUtil.validateTextField(this.name, "name", newErrors);
    CoreInputModelUtil.validateTextField(
      this.promoCode,
      "promoCode",
      newErrors
    );
    if (this.amountType === "Amount") {
      CoreInputModelUtil.validateDecimalValue(
        this.amount.toString(),
        "amount",
        newErrors,
        0,
        false,
        "Please enter a valid amount."
      );
    } else {
      if (this.amount < 1 || this.amount > 100) {
        newErrors.push({
          field: "amount",
          message: "Please enter a valid percentage between 1 and 100.",
        });
      }
      CoreInputModelUtil.validateDecimalValue(
        this.maximumAmount.toString(),
        "maximumAmount",
        newErrors,
        0,
        true,
        "Please enter a valid amount."
      );
    }
    if (this.minimumPurchase && this.minimumPurchase > 0) {
      CoreInputModelUtil.validateDecimalValue(
        this.minimumPurchase.toString(),
        "minimumPurchase",
        newErrors,
        0,
        true,
        "Please enter a valid amount."
      );
    }
    if (this.totalUsage && this.totalUsage > 0) {
      CoreInputModelUtil.validateDecimalValue(
        this.totalUsage.toString(),
        "totalUsage",
        newErrors,
        0,
        true,
        "Please enter a valid total usage count."
      );
    }
    if (this.beginsDate) {
      CoreInputModelUtil.validateDateField(
        "beginsDate",
        this.beginsDate.toISOString(),
        newErrors
      );
      CoreInputModelUtil.validateTime(
        this.beginsTime,
        "beginsTime",
        this.beginsMeredium,
        newErrors
      );
    }
    if (this.endsDate) {
      CoreInputModelUtil.validateTime(
        this.endsTime,
        "endsTime",
        this.endsMeredium,
        newErrors
      );

      CoreInputModelUtil.validateDateAndTime(
        nameof<PromotionModel>("endsDate"),
        nameof<PromotionModel>("endsTime"),
        this.endsDate,
        this.endsTime,
        this.endsMeredium,
        newErrors,
        0
      );
    }
    if (this.beginsDate && this.endsDate) {
      const beginsDateTime = new Date(this.beginsDate);
      const endsDateTime = new Date(this.endsDate);

      const [beginsHour, beginsMinute] = this.beginsTime.split(":").map(Number);
      const [endsHour, endsMinute] = this.endsTime.split(":").map(Number);

      beginsDateTime.setHours(
        this.beginsMeredium === "PM" && beginsHour !== 12
          ? beginsHour + 12
          : this.beginsMeredium === "AM" && beginsHour === 12
            ? 0
            : beginsHour,
        beginsMinute
      );

      endsDateTime.setHours(
        this.endsMeredium === "PM" && endsHour !== 12
          ? endsHour + 12
          : this.endsMeredium === "AM" && endsHour === 12
            ? 0
            : endsHour,
        endsMinute
      );

      if (endsDateTime <= beginsDateTime) {
        newErrors.push({
          field: "endsDate",
          message: "End date and time must be after start date and time.",
        });
        newErrors.push({
          field: "endsTime",
          message: "",
        });
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  hasError(field: keyof PromotionModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  static deserializeAPIModel(data?: IAPIPromotionModel): PromotionModel {
    if (!data) return new PromotionModel();
    const beginsDate = data.startDate
      ? new Date(UtilityMethods.removeZFromDateString(data.startDate as string))
      : null;
    const beginsTime = beginsDate
      ? UtilityMethods.getHHMMFromDate(beginsDate)
      : "12:00";
    const endsDate = data.endDate
      ? new Date(UtilityMethods.removeZFromDateString(data.endDate as string))
      : null;
    const endsTime = endsDate
      ? UtilityMethods.getHHMMFromDate(endsDate)
      : "11:59";
    const beginsMeredium = beginsDate
      ? beginsDate.getHours() >= 12
        ? Merediem.PM
        : Merediem.AM
      : Merediem.AM;
    const endsMeredium = endsDate
      ? endsDate.getHours() >= 12
        ? Merediem.PM
        : Merediem.AM
      : Merediem.PM;

    const mappedData: Partial<PromotionModel> = {
      id: data?.id || "",
      name: data?.name || "",
      promoCode: data?.code || "",
      amount: data?.discountValue || 0,
      amountType: data?.discountType as keyof typeof AmountType,
      freeShipping: data?.freeShipping || false,
      waiveSurcharge: data?.waiveSurcharge || false,
      minimumPurchase: data?.minimumPurchase || 0,
      totalUsage: data?.totalUsage || 0,
      beginsDate: beginsDate,
      beginsTime: beginsTime,
      beginsMeredium: beginsMeredium,
      endsDate: endsDate,
      endsTime: endsTime,
      endsMeredium: endsMeredium,
      lastUsed: data?.lastUsed
        ? new Date(UtilityMethods.removeZFromDateString(data.lastUsed))
        : null,
      maximumAmount: data?.maximumDiscountedAmount || 0,
    };
    return new PromotionModel(mappedData);
  }

  createAPIModel(): IAPIPromotionModel {
    return {
      accountId: AuthService.authData.accountId,
      name: this.name,
      code: this.promoCode,
      discountValue: this.amount,
      discountType: this.amountType,
      freeShipping: this.freeShipping,
      waiveSurcharge: this.waiveSurcharge,
      minimumPurchase: this.minimumPurchase || null,
      totalUsage: this.totalUsage || null,
      startDate: this.beginsDate
        ? UtilityMethods.addTimeToDate(
            this.beginsDate ? new Date(this.beginsDate) : new Date(),
            this.beginsTime,
            this.beginsMeredium
          )
        : null,
      endDate: this.endsDate
        ? UtilityMethods.addTimeToDate(
            this.endsDate ? new Date(this.endsDate) : new Date(),
            this.endsTime,
            this.endsMeredium
          )
        : null,
      maximumDiscountedAmount: this.maximumAmount ? this.maximumAmount : null,
    };
  }

  createAPIModelForUpdate(
    isActive: boolean = false,
    id: string
  ): IAPIUpdatePromotionModel {
    return {
      ...this.createAPIModel(),
      isActive: isActive,
      id: id,
    };
  }

  createAPIModelToTestUniqueness(id: string): IAPICheckUniquesnessModel {
    return {
      accountId: AuthService.authData.accountId,
      code: this.promoCode,
      id: id || null,
    };
  }
}
